import axios from "axios";

// Small opt-in client-side cache for read-only API calls (GET, and POST endpoints that only read).
// Not wired into the global axios instance: callers choose it explicitly.
//
// - Entries are keyed by exactly the credentials the axios interceptor sends (hash of the JWT +
//   provider + account), so one user's data can never be served to another. No JWT: no caching.
// - Concurrent identical requests share one in-flight promise.
// - Errors and non-2xx responses are never cached.
// - Every caller gets its own deep copy, so in-place mutation (e.g. data.sort) cannot corrupt the cache.

const DEFAULT_TTL_MS = 60000;
const MAX_ENTRIES = 100;

const cache = new Map(); // key -> {expiresAt, payload} | {promise}

const storage = () =>
  typeof window !== "undefined" ? window.sessionStorage : null;

// Short non-cryptographic hash (FNV-1a + djb2, 64 bits total) so the token itself is never kept in a key.
const hashToken = token => {
  let fnv = 0x811c9dc5;
  let djb = 5381;
  for (let i = 0; i < token.length; i++) {
    const c = token.charCodeAt(i);
    fnv = Math.imul(fnv ^ c, 0x01000193) >>> 0;
    djb = (Math.imul(djb, 33) + c) >>> 0;
  }
  return fnv.toString(36) + djb.toString(36) + token.length.toString(36);
};

// Whose data the API returns: the same values the axios interceptor (src/index.js) attaches to the request.
// One read of the credentials. The cache key AND the request headers are both
// derived from this snapshot, so a response can only ever be stored under the
// identity whose credentials actually fetched it.
const readCredentials = () => {
  const s = storage();
  const jwt = s && s.getItem("jwt");
  if (!jwt) return null;
  return {
    jwt,
    provider: s.getItem("x-auth-provider"),
    refreshToken: s.getItem("x-refresh-token"),
    account: s.getItem("account")
  };
};

const identityOf = creds =>
  creds
    ? JSON.stringify([
        hashToken(creds.jwt),
        creds.provider || "",
        creds.account || ""
      ])
    : null;

export const getCacheIdentity = () => identityOf(readCredentials());

// Same headers as the global interceptor in src/index.js — keep the two in sync.
const headersOf = creds => ({
  Authorization: "Bearer " + creds.jwt,
  "x-auth-provider": creds.provider,
  "x-refresh-token": creds.refreshToken,
  account: creds.account
});

// Own instance: the global request interceptor would re-read sessionStorage a
// tick later and could send different credentials than the key was built from.
const http = axios.create();

const normalise = value => {
  if (value === null || value === undefined) return value;
  if (typeof value === "function") return undefined;
  if (typeof value.toJSON === "function") return normalise(value.toJSON()); // Date, moment, dayjs -> ISO string
  if (Array.isArray(value))
    return value.map(v => (v === undefined ? null : normalise(v)));
  if (typeof value === "object") {
    const result = {};
    Object.keys(value)
      .sort()
      .forEach(k => {
        const v = normalise(value[k]);
        if (v !== undefined) result[k] = v;
      });
    return result;
  }
  return value;
};

export const stableStringify = value =>
  JSON.stringify(normalise(value)) || "null";

const buildKey = (method, url, body, config, identity = getCacheIdentity()) => {
  if (!identity) return null;
  return [
    identity,
    method,
    url,
    stableStringify(config && config.params),
    stableStringify(body)
  ].join("|");
};

const clone = data =>
  data === undefined ? data : JSON.parse(JSON.stringify(data));

const prune = now => {
  cache.forEach((entry, key) => {
    if (entry.payload && entry.expiresAt <= now) cache.delete(key);
  });
  while (cache.size > MAX_ENTRIES) {
    cache.delete(cache.keys().next().value); // Map iterates in insertion order: oldest first
  }
};

const run = (method, url, body, config, ttlMs, fallback, send) => {
  const creds = readCredentials();
  const apiUrl = process.env.REACT_APP_API_URL;
  // Only API calls carry these credentials; anything else is not cached.
  if (
    !creds ||
    !apiUrl ||
    !String(url).startsWith(apiUrl) ||
    (config && config.auth)
  )
    return fallback();
  const key = buildKey(method, url, body, config, identityOf(creds));
  if (!key) return fallback();
  const withCreds = {
    ...(config || {}),
    headers: { ...((config && config.headers) || {}), ...headersOf(creds) }
  };

  const now = Date.now();
  const hit = cache.get(key);
  if (hit && hit.payload && hit.expiresAt > now) {
    return Promise.resolve({
      data: clone(hit.payload.data),
      status: hit.payload.status
    });
  }
  if (hit && hit.promise) {
    return hit.promise.then(payload => ({
      data: clone(payload.data),
      status: payload.status
    }));
  }

  const promise = send(withCreds).then(response => {
    if (response.status < 200 || response.status >= 300) {
      throw Object.assign(new Error("Unexpected status " + response.status), {
        response
      });
    }
    return { data: clone(response.data), status: response.status };
  });
  cache.set(key, { promise });
  prune(now);

  promise.then(
    payload => {
      if (cache.get(key) && cache.get(key).promise === promise) {
        cache.delete(key); // re-insert so it counts as newest
        cache.set(key, { expiresAt: Date.now() + ttlMs, payload });
        prune(Date.now());
      }
    },
    () => {
      if (cache.get(key) && cache.get(key).promise === promise)
        cache.delete(key);
    }
  );

  return promise.then(payload => ({
    data: clone(payload.data),
    status: payload.status
  }));
};

export const cachedGet = (url, config, { ttlMs = DEFAULT_TTL_MS } = {}) =>
  run(
    "GET",
    url,
    undefined,
    config,
    ttlMs,
    () => axios.get(url, config),
    cfg => http.get(url, cfg)
  );

export const cachedPost = (
  url,
  body,
  config,
  { ttlMs = DEFAULT_TTL_MS } = {}
) =>
  run(
    "POST",
    url,
    body,
    config,
    ttlMs,
    () => axios.post(url, body, config),
    cfg => http.post(url, body, cfg)
  );

// GET that never reads or writes the cache (for polling), with the same credentials/headers as cachedGet.
export const freshGet = (url, config) => {
  const creds = readCredentials();
  const apiUrl = process.env.REACT_APP_API_URL;
  if (!creds || !apiUrl || !String(url).startsWith(apiUrl) || (config && config.auth))
    return axios.get(url, config);
  return http.get(url, {
    ...(config || {}),
    headers: { ...((config && config.headers) || {}), ...headersOf(creds) }
  });
};

const peek = (method, url, body, config) => {
  const key = buildKey(method, url, body, config);
  const hit = key && cache.get(key);
  if (hit && hit.payload && hit.expiresAt > Date.now()) {
    return { data: clone(hit.payload.data), status: hit.payload.status };
  }
  return null;
};

// Synchronous lookup (no request): {data} when a fresh entry exists, otherwise null.
export const getCachedGet = (url, config) =>
  peek("GET", url, undefined, config);
export const getCachedPost = (url, body, config) =>
  peek("POST", url, body, config);

// Small in-memory store (never persisted) for page state that should survive navigation within the SPA,
// e.g. the last dashboard snapshot. Emptied together with the request cache (logout / account switch).
const memory = new Map();
export const getMemory = key => memory.get(key);
export const setMemory = (key, value) => {
  memory.set(key, value);
};

// The selected account alone (unlike getCacheIdentity it ignores silent JWT renewals).
export const getAccountKey = () => {
  const s = storage();
  return (s && s.getItem("account")) || "";
};

export const clearRequestCache = () => {
  cache.clear();
  memory.clear();
};
